import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Loader2, Play, Send, Clock, Download, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { toCsv, downloadCsv } from "@/lib/csv";

type Campaign = any;
type Step = any;

export default function AdminCampaigns() {
  const [rows, setRows] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<Campaign | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [runs, setRuns] = useState<any[]>([]);
  const [engagements, setEngagements] = useState<any[]>([]);
  const [cron, setCron] = useState<any | null>(null);
  const [testFor, setTestFor] = useState<Step | null>(null);
  const [testLeads, setTestLeads] = useState<{ id: string; email: string; full_name: string | null }[]>([]);
  const [testSelected, setTestSelected] = useState<Set<string>>(new Set());
  const [testExtra, setTestExtra] = useState("");
  const [testBusy, setTestBusy] = useState(false);

  // Engagement filters & sort
  const [filterFrom, setFilterFrom] = useState<string>("");
  const [filterTo, setFilterTo] = useState<string>("");
  const [filterStepId, setFilterStepId] = useState<string>("__all");
  const [filterSource, setFilterSource] = useState<string>("__all");
  const [sortKey, setSortKey] = useState<"sent_at" | "recipient_email" | "opens_count" | "clicks_count" | "source">("sent_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  async function load() {
    setLoading(true);
    const [{ data }, cronRes] = await Promise.all([
      supabase.from("email_campaigns").select("*").order("created_at", { ascending: false }),
      supabase.rpc("get_cron_status" as any, { _name: "campaign-worker-every-minute" }),
    ]);
    setRows(data ?? []);
    setCron(((cronRes.data as any[]) ?? [])[0] ?? null);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function openCampaign(c: Campaign) {
    setOpen(c);
    setFilterFrom(""); setFilterTo(""); setFilterStepId("__all"); setFilterSource("__all");
    setSortKey("sent_at"); setSortDir("desc");
    const [{ data: s }, { data: r }, { data: eng }] = await Promise.all([
      supabase.from("email_campaign_steps").select("*").eq("campaign_id", c.id).order("position"),
      supabase.from("email_campaign_runs").select("*").eq("campaign_id", c.id).order("started_at", { ascending: false }).limit(50),
      supabase.from("email_engagements").select("*").eq("campaign_id", c.id).order("sent_at", { ascending: false }).limit(500),
    ]);
    setSteps(s ?? []);
    setRuns(r ?? []);
    setEngagements(eng ?? []);
  }

  const filteredEng = useMemo(() => {
    const fromMs = filterFrom ? new Date(filterFrom).getTime() : -Infinity;
    const toMs = filterTo ? new Date(filterTo).getTime() + 86400000 : Infinity;
    const out = engagements.filter(e => {
      const t = new Date(e.sent_at).getTime();
      if (t < fromMs || t > toMs) return false;
      if (filterStepId !== "__all" && e.step_id !== filterStepId) return false;
      if (filterSource !== "__all" && e.source !== filterSource) return false;
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    return [...out].sort((a, b) => {
      const av: any = a[sortKey]; const bv: any = b[sortKey];
      if (sortKey === "sent_at") return (new Date(av).getTime() - new Date(bv).getTime()) * dir;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av ?? "").localeCompare(String(bv ?? "")) * dir;
    });
  }, [engagements, filterFrom, filterTo, filterStepId, filterSource, sortKey, sortDir]);

  function toggleSort(k: typeof sortKey) {
    if (k === sortKey) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir(k === "recipient_email" || k === "source" ? "asc" : "desc"); }
  }

  function exportEngagementsCsv() {
    if (!open) return;
    const stepMap = new Map(steps.map((s: any) => [s.id, s]));
    const rows = filteredEng.map(e => {
      const s: any = stepMap.get(e.step_id) ?? {};
      const lastClick = (e.clicked_urls ?? []).slice(-1)[0]?.url ?? "";
      return {
        campaign: open.name,
        step_position: (s.position ?? 0) + 1,
        step_subject: s.subject ?? "",
        recipient_email: e.recipient_email,
        source: e.source,
        sent_at: e.sent_at,
        opens_count: e.opens_count,
        last_opened_at: e.last_opened_at ?? "",
        clicks_count: e.clicks_count,
        last_clicked_at: e.last_clicked_at ?? "",
        last_clicked_url: lastClick,
        status: e.opens_count > 0 || e.clicks_count > 0 ? "engaged" : "delivered",
      };
    });
    const csv = toCsv(rows);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`engagements-${open.name.replace(/\W+/g, "_")}-${stamp}.csv`, csv);
    toast.success(`Exported ${rows.length} row(s)`);
  }

  async function newCampaign() {
    const { data, error } = await supabase.from("email_campaigns").insert({
      name: "New campaign", trigger_type: "lead_created", status: "draft",
    }).select().single();
    if (error) return toast.error(error.message);
    load();
    openCampaign(data);
  }

  async function saveCampaign() {
    if (!open) return;
    const { error } = await supabase.from("email_campaigns").update({
      name: open.name, description: open.description, status: open.status,
      trigger_type: open.trigger_type, trigger_config: open.trigger_config ?? {},
    }).eq("id", open.id);
    if (error) return toast.error(error.message);
    toast.success("Saved"); load();
  }

  async function addStep() {
    if (!open) return;
    const pos = steps.length;
    const { data, error } = await supabase.from("email_campaign_steps").insert({
      campaign_id: open.id, position: pos, delay_minutes: pos === 0 ? 0 : 1440,
      subject: `Step ${pos + 1}`, body_html: "<p>Hello {{name}},</p>",
    }).select().single();
    if (error) return toast.error(error.message);
    setSteps([...steps, data]);
  }

  async function saveStep(s: Step) {
    const { error } = await supabase.from("email_campaign_steps").update({
      subject: s.subject, body_html: s.body_html, delay_minutes: s.delay_minutes,
    }).eq("id", s.id);
    if (error) return toast.error(error.message);
    toast.success("Step saved");
  }

  async function delStep(id: string) {
    await supabase.from("email_campaign_steps").delete().eq("id", id);
    setSteps(steps.filter(s => s.id !== id));
  }

  async function delCampaign(id: string) {
    if (!confirm("Delete campaign?")) return;
    await supabase.from("email_campaigns").delete().eq("id", id);
    setOpen(null); load();
  }

  async function runWorker() {
    const { error } = await supabase.functions.invoke("campaign-worker", { body: { trigger: "manual" } });
    if (error) return toast.error(error.message);
    toast.success("Worker ran");
    load();
  }

  async function openTest(step: Step) {
    setTestFor(step);
    setTestSelected(new Set());
    setTestExtra("");
    const { data } = await supabase.from("leads").select("id,email,full_name").order("last_activity_at", { ascending: false }).limit(50);
    setTestLeads((data as any) ?? []);
  }

  async function sendTest() {
    if (!testFor) return;
    setTestBusy(true);
    const override_emails = testExtra.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    const { data, error } = await supabase.functions.invoke("campaign-step-test", {
      body: { step_id: testFor.id, lead_ids: [...testSelected], override_emails },
    });
    setTestBusy(false);
    if (error || (data as any)?.error) return toast.error((data as any)?.error ?? error?.message ?? "Test failed");
    toast.success(`Test sent to ${(data as any).sent} recipient(s)`);
    setTestFor(null);
  }

  function nextRunLabel(): string {
    if (!cron) return "Not scheduled";
    if (!cron.active) return `Paused (${cron.schedule})`;
    // schedule is every minute → next run is start of next minute
    const last = cron.last_start ? new Date(cron.last_start) : null;
    const next = new Date(Math.ceil(Date.now() / 60000) * 60000);
    return `Next run ${next.toLocaleTimeString()} · ${cron.schedule}` + (last ? ` · last ${cron.last_status} at ${last.toLocaleTimeString()}` : "");
  }

  return (
    <AdminShell title="Email Campaigns" actions={
      <>
        <Button variant="outline" size="sm" onClick={runWorker}><Play className="w-4 h-4 mr-1" /> Run worker</Button>
        <Button size="sm" onClick={newCampaign}><Plus className="w-4 h-4 mr-1" /> New Campaign</Button>
      </>
    }>
      <div className="flex items-center gap-2 mb-4 text-xs font-mono bg-panel border border-border px-3 py-2">
        <Clock className="w-3.5 h-3.5 text-primary" />
        <span className="text-muted-foreground">cron:</span>
        <span>{nextRunLabel()}</span>
      </div>
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
        <div className="bg-surface border border-border">
          <table className="w-full text-sm">
            <thead className="bg-panel"><tr><th className="text-left p-2">Name</th><th className="text-left p-2">Trigger</th><th className="text-left p-2">Status</th><th></th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-2">{r.name}</td>
                  <td className="p-2 font-mono text-xs">{r.trigger_type}</td>
                  <td className="p-2">{r.status}</td>
                  <td className="p-2 flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openCampaign(r)}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => delCampaign(r.id)}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No campaigns yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <Dialog open onOpenChange={(o) => !o && setOpen(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Campaign</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input value={open.name} onChange={e => setOpen({ ...open, name: e.target.value })} /></div>
              <div className="col-span-2"><Label>Description</Label><Input value={open.description ?? ""} onChange={e => setOpen({ ...open, description: e.target.value })} /></div>
              <div><Label>Trigger</Label>
                <Select value={open.trigger_type} onValueChange={v => setOpen({ ...open, trigger_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lead_created">Lead created</SelectItem>
                    <SelectItem value="booking_abandoned">Booking abandoned</SelectItem>
                    <SelectItem value="source_match">Source matches…</SelectItem>
                    <SelectItem value="tag_added">Tag added</SelectItem>
                    <SelectItem value="manual">Manual segment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={open.status} onValueChange={v => setOpen({ ...open, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {open.trigger_type === "source_match" && (
                <div className="col-span-2"><Label>Source value (e.g. contact_form, landing_page, booking_abandoned)</Label>
                  <Input value={open.trigger_config?.source ?? ""} onChange={e => setOpen({ ...open, trigger_config: { ...(open.trigger_config ?? {}), source: e.target.value } })} /></div>
              )}
              {open.trigger_type === "tag_added" && (
                <div className="col-span-2"><Label>Tag</Label>
                  <Input value={open.trigger_config?.tag ?? ""} onChange={e => setOpen({ ...open, trigger_config: { ...(open.trigger_config ?? {}), tag: e.target.value } })} /></div>
              )}
            </div>
            <div className="mt-3 flex justify-end"><Button size="sm" onClick={saveCampaign}>Save campaign</Button></div>

            <h3 className="mt-6 font-display text-lg">Steps</h3>
            <div className="space-y-3 mt-2">
              {steps.map((s, i) => {
                const stepEng = engagements.filter(e => e.step_id === s.id);
                const sent = stepEng.filter(e => e.source === "campaign").length;
                const tests = stepEng.filter(e => e.source === "test").length;
                const opens = stepEng.filter(e => e.opens_count > 0).length;
                const clicks = stepEng.filter(e => e.clicks_count > 0).length;
                const openRate = sent ? ((opens / sent) * 100).toFixed(1) : "—";
                const clickRate = sent ? ((clicks / sent) * 100).toFixed(1) : "—";
                return (
                <div key={s.id} className="bg-panel border border-border p-3 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">Step {i + 1}</div>
                  <div className="flex flex-wrap gap-3 text-[11px] font-mono text-muted-foreground border-b border-border pb-2">
                    <span>sent <strong className="text-foreground">{sent}</strong></span>
                    <span>opens <strong className="text-foreground">{opens}</strong> ({openRate}%)</span>
                    <span>clicks <strong className="text-foreground">{clicks}</strong> ({clickRate}%)</span>
                    <span>tests <strong className="text-foreground">{tests}</strong></span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Delay (minutes)</Label><Input type="number" value={s.delay_minutes} onChange={e => setSteps(steps.map(x => x.id === s.id ? { ...x, delay_minutes: Number(e.target.value) } : x))} /></div>
                    <div><Label>Subject</Label><Input value={s.subject} onChange={e => setSteps(steps.map(x => x.id === s.id ? { ...x, subject: e.target.value } : x))} /></div>
                  </div>
                  <div><Label>Body HTML — supports {`{{name}}`}, {`{{email}}`}, {`{{promo}}`}</Label>
                    <Textarea rows={5} value={s.body_html} onChange={e => setSteps(steps.map(x => x.id === s.id ? { ...x, body_html: e.target.value } : x))} /></div>
                  {s.body_html && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-muted-foreground">Preview rendered HTML</summary>
                      <div className="mt-2 border border-border bg-background p-3 max-h-64 overflow-auto"
                        dangerouslySetInnerHTML={{ __html: String(s.body_html).split("{{name}}").join("Jane").split("{{email}}").join("jane@example.com") }} />
                    </details>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => delStep(s.id)}><Trash2 className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => openTest(s)}><Send className="w-4 h-4 mr-1" /> Send test</Button>
                    <Button size="sm" onClick={() => saveStep(s)}>Save</Button>
                  </div>
                </div>
              ); })}
              <Button variant="outline" size="sm" onClick={addStep}><Plus className="w-4 h-4 mr-1" /> Add step</Button>
            </div>

            <div className="mt-6 flex items-end justify-between gap-3 flex-wrap">
              <h3 className="font-display text-lg">Delivery log</h3>
              <Button size="sm" variant="outline" onClick={exportEngagementsCsv} disabled={filteredEng.length === 0}>
                <Download className="w-4 h-4 mr-1" /> CSV ({filteredEng.length})
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
              <div><Label className="text-[10px] uppercase tracking-widest">From</Label>
                <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} /></div>
              <div><Label className="text-[10px] uppercase tracking-widest">To</Label>
                <Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} /></div>
              <div><Label className="text-[10px] uppercase tracking-widest">Step</Label>
                <Select value={filterStepId} onValueChange={setFilterStepId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All steps</SelectItem>
                    {steps.map((s: any, i: number) => <SelectItem key={s.id} value={s.id}>Step {i + 1}: {s.subject}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-[10px] uppercase tracking-widest">Source</Label>
                <Select value={filterSource} onValueChange={setFilterSource}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All</SelectItem>
                    <SelectItem value="campaign">Campaign send</SelectItem>
                    <SelectItem value="test">Test send</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="bg-surface border border-border max-h-72 overflow-auto text-xs mt-2">
              <table className="w-full">
                <thead className="bg-panel sticky top-0">
                  <tr>
                    {([
                      ["recipient_email", "Recipient"],
                      ["source", "Source"],
                      ["sent_at", "Sent"],
                      ["opens_count", "Opens"],
                      ["clicks_count", "Clicks"],
                    ] as const).map(([k, label]) => (
                      <th key={k} className="text-left p-2 cursor-pointer select-none" onClick={() => toggleSort(k as any)}>
                        <span className="inline-flex items-center gap-1">{label} <ArrowUpDown className="w-3 h-3 opacity-50" />
                          {sortKey === k && <span className="text-[10px]">{sortDir}</span>}</span>
                      </th>
                    ))}
                    <th className="text-left p-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEng.length === 0 ? (
                    <tr><td colSpan={6} className="p-3 text-muted-foreground text-center">No deliveries match the current filters.</td></tr>
                  ) : filteredEng.slice(0, 500).map(e => (
                    <tr key={e.id} className="border-t border-border">
                      <td className="p-2 font-mono truncate max-w-[220px]" title={e.recipient_email}>{e.recipient_email}</td>
                      <td className="p-2">{e.source === "test" ? <span className="text-amber-400">test</span> : "campaign"}</td>
                      <td className="p-2 text-muted-foreground">{new Date(e.sent_at).toLocaleString()}</td>
                      <td className="p-2">{e.opens_count}</td>
                      <td className="p-2">{e.clicks_count}</td>
                      <td className="p-2">{e.clicks_count > 0 ? <span className="text-fuchsia-400">clicked</span>
                        : e.opens_count > 0 ? <span className="text-sky-400">opened</span>
                        : <span className="text-emerald-500">delivered</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredEng.length > 500 && <div className="p-2 text-[10px] text-muted-foreground">Showing first 500 of {filteredEng.length} — narrow filters or export CSV for full set.</div>}
            </div>

            <h3 className="mt-6 font-display text-lg">Recent runs</h3>
            <div className="bg-surface border border-border max-h-48 overflow-y-auto">
              {runs.length === 0 ? <div className="p-3 text-xs text-muted-foreground">No runs yet.</div> : runs.map(r => (
                <div key={r.id} className="p-2 border-b border-border text-xs flex justify-between">
                  <span>Lead {r.lead_id?.slice(0, 8)} · step {r.current_step}</span>
                  <span className="text-muted-foreground">{r.status} · {new Date(r.started_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
            <DialogFooter><Button onClick={() => setOpen(null)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {testFor && (
        <Dialog open onOpenChange={(o) => !o && setTestFor(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Send test — {testFor.subject}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Pick leads (recent 50)</Label>
                <div className="border border-border max-h-60 overflow-y-auto mt-1">
                  {testLeads.map(l => (
                    <label key={l.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-border text-sm cursor-pointer hover:bg-panel">
                      <input type="checkbox" checked={testSelected.has(l.id)} onChange={() => {
                        const next = new Set(testSelected);
                        next.has(l.id) ? next.delete(l.id) : next.add(l.id);
                        setTestSelected(next);
                      }} />
                      <span className="font-mono text-xs text-muted-foreground">{l.email}</span>
                      <span>{l.full_name ?? ""}</span>
                    </label>
                  ))}
                  {testLeads.length === 0 && <div className="p-3 text-xs text-muted-foreground">No leads yet.</div>}
                </div>
              </div>
              <div>
                <Label>Additional emails (comma or space separated)</Label>
                <Input placeholder="me@example.com, qa@example.com" value={testExtra} onChange={e => setTestExtra(e.target.value)} />
              </div>
              <p className="text-xs text-muted-foreground">Test emails are prefixed [TEST] and clearly marked — they do not affect campaign runs or stats.</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setTestFor(null)}>Cancel</Button>
              <Button onClick={sendTest} disabled={testBusy || (testSelected.size === 0 && !testExtra.trim())}>
                {testBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Send test</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AdminShell>
  );
}